import { observer } from "mobx-react-lite";
import { FC } from "react";
import { ItemListSkeletonRowData } from "@/components/item-list/types";
import { MDSSkeleton } from "@/mds";
import styled from "@emotion/styled";

export interface ItemListSkeletonRowProps {
  data: ItemListSkeletonRowData;
}

export const ItemListSkeletonRow: FC<ItemListSkeletonRowProps> = observer(() => {
  return (
    <>
      <Row>
        <Left>
          <SquareSkeleton width={18} height={18} />
        </Left>
        <Center>
          <LabelLine>
            <TitleSkeleton width={220} height={20} />
            <TitleIconSkeleton width={16} height={16} />
          </LabelLine>
          <PreviewSkeleton width={320} height={14} />
        </Center>
        <Right>
          <DateSkeleton width={40} height={14} />
        </Right>
      </Row>
      <StyledRowSeparator />
    </>
  );
});

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-inline: ${({ theme }) => theme.spacing["7"]};
  padding-block: ${({ theme }) => theme.spacing["4"]};
  min-height: 60px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.list.default};
  user-select: none;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.list.hover};
    transition: 0.2s all;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin-right: 10px;
  align-self: stretch;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const LabelLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const TitleSkeleton = styled(MDSSkeleton)`
  max-width: 60%;
`;

const TitleIconSkeleton = styled(MDSSkeleton)`
  border-radius: 4px;
`;

const SquareSkeleton = styled(MDSSkeleton)`
  border-radius: 4px;
`;

const PreviewSkeleton = styled(MDSSkeleton)`
  max-width: 70%;
`;

const Right = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex: 0 0 auto;
  margin-left: ${({ theme }) => theme.spacing["5"]};
  align-self: stretch;
`;

const DateSkeleton = styled(MDSSkeleton)`
  border-radius: 4px;
`;

export const StyledRowSeparator = styled.div`
  /* Match the list row separator spacing */
  position: relative;
  margin: ${({ theme }) => `-1px ${theme.spacing["7"]} 0 ${theme.spacing["7"]}`};
  background-color: ${({ theme }) => theme.colors.border.default};
  height: 1px;
  width: calc(100% - 2 * ${({ theme }) => theme.spacing["7"]});
`;
